// @ts-nocheck
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { parseLocalDate } from "@/utils/formatters";
import { useOrganizationContext } from "@/contexts/OrganizationContext";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceEventId?: string;
  sourceEventName?: string;
}

interface EventOption {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
}

interface SourceCounts {
  divisions: number;
  pricing: number;
  extras: number;
  coupons: number;
  forms: number;
  recipients: number;
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return "Date TBD";
  try {
    const s = format(parseLocalDate(start), "MMM d, yyyy");
    if (end && end !== start) {
      const e = format(parseLocalDate(end), "MMM d, yyyy");
      return `${s} – ${e}`;
    }
    return s;
  } catch {
    return "Date TBD";
  }
}

export function CloneEventDialog({
  open,
  onOpenChange,
  sourceEventId: prefillId,
  sourceEventName: prefillName,
}: Props) {
  const navigate = useNavigate();
  const { organization } = useOrganizationContext();

  // ── Source selection state ──
  const [events, setEvents] = useState<EventOption[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [sourceCounts, setSourceCounts] = useState<SourceCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);

  // ── Form state ──
  const [newName, setNewName] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [cloning, setCloning] = useState(false);

  const effectiveSourceId = prefillId || selectedId;
  const effectiveSourceName =
    prefillId && prefillName
      ? prefillName
      : events.find((e) => e.id === selectedId)?.name || "";

  // ── Fetch org events for picker (only when no prefillId) ──
  useEffect(() => {
    if (!open || prefillId || !organization?.id) return;

    const fetchEvents = async () => {
      setEventsLoading(true);
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date, end_date")
        .eq("organization_id", organization.id)
        .is("deleted_at", null)
        .order("start_date", { ascending: false });

      if (error) {
        console.error("Error fetching events:", error);
      } else {
        setEvents((data as EventOption[]) || []);
      }
      setEventsLoading(false);
    };
    fetchEvents();
  }, [open, prefillId, organization?.id]);

  // ── Fetch source counts when source changes ──
  useEffect(() => {
    if (!effectiveSourceId) {
      setSourceCounts(null);
      return;
    }

    const fetchCounts = async () => {
      setCountsLoading(true);
      const id = effectiveSourceId;

      const [divisions, pricing, extras, coupons, forms, recipients] =
        await Promise.all([
          supabase
            .from("event_divisions")
            .select("id", { count: "exact", head: true })
            .eq("event_id", id),
          supabase
            .from("event_pricing")
            .select("id", { count: "exact", head: true })
            .eq("event_id", id),
          supabase
            .from("event_extras")
            .select("id", { count: "exact", head: true })
            .eq("event_id", id),
          supabase
            .from("coupons")
            .select("id", { count: "exact", head: true })
            .eq("event_id", id),
          supabase
            .from("registration_forms")
            .select("id", { count: "exact", head: true })
            .eq("event_id", id),
          supabase
            .from("notification_recipients")
            .select("id", { count: "exact", head: true })
            .eq("event_id", id),
        ]);

      setSourceCounts({
        divisions: divisions.count ?? 0,
        pricing: pricing.count ?? 0,
        extras: extras.count ?? 0,
        coupons: coupons.count ?? 0,
        forms: forms.count ?? 0,
        recipients: recipients.count ?? 0,
      });
      setCountsLoading(false);
    };
    fetchCounts();
  }, [effectiveSourceId]);

  // ── Pre-fill name from source ──
  useEffect(() => {
    if (effectiveSourceName && !newName) {
      setNewName(`${effectiveSourceName} (Copy)`);
    }
  }, [effectiveSourceName]);

  // ── Reset on close ──
  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setSelectedId("");
      setSourceCounts(null);
      setNewName("");
      setNewStartDate("");
      setNewEndDate("");
    }
    onOpenChange(v);
  };

  // ── Validation ──
  const validate = (): string | null => {
    if (!effectiveSourceId) return "Select a source event.";
    if (!newName.trim()) return "Event name is required.";
    if (!newStartDate) return "Start date is required.";
    if (!newEndDate) return "End date is required.";
    if (newEndDate < newStartDate)
      return "End date must be on or after start date.";
    return null;
  };

  // ── Submit ──
  const handleClone = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setCloning(true);

    const { data, error } = await supabase.rpc("clone_event", {
      p_source_event_id: effectiveSourceId,
      p_new_name: newName.trim(),
      p_new_start_date: newStartDate,
      p_new_end_date: newEndDate,
    });

    if (error) {
      console.error("Error cloning event:", error);
      toast.error(error.message || "Failed to clone event");
      setCloning(false);
      return;
    }

    toast.success("Event cloned! Redirecting to edit...");
    setCloning(false);
    handleOpenChange(false);
    navigate(`/admin/events/${data}/manage`);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Clone event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Source picker or read-only line */}
          {prefillId ? (
            <div className="text-sm">
              <span className="text-muted-foreground">Cloning from: </span>
              <span className="font-medium">{prefillName}</span>
            </div>
          ) : (
            <div className="space-y-1">
              <label className="text-sm font-medium">Source event</label>
              {eventsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading events...
                </div>
              ) : (
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an event to clone..." />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} — {formatDateRange(e.start_date, e.end_date)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Source counts preview */}
          {effectiveSourceId && (
            <div className="bg-muted/50 rounded-md p-3">
              {countsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading config summary...
                </div>
              ) : sourceCounts ? (
                <div className="flex flex-wrap gap-2">
                  {sourceCounts.divisions > 0 && (
                    <Badge variant="outline">{sourceCounts.divisions} divisions</Badge>
                  )}
                  {sourceCounts.pricing > 0 && (
                    <Badge variant="outline">{sourceCounts.pricing} pricing tiers</Badge>
                  )}
                  {sourceCounts.extras > 0 && (
                    <Badge variant="outline">{sourceCounts.extras} extras</Badge>
                  )}
                  {sourceCounts.coupons > 0 && (
                    <Badge variant="outline">{sourceCounts.coupons} coupons</Badge>
                  )}
                  {sourceCounts.forms > 0 && (
                    <Badge variant="outline">{sourceCounts.forms} custom forms</Badge>
                  )}
                  {sourceCounts.recipients > 0 && (
                    <Badge variant="outline">
                      {sourceCounts.recipients} notification recipients
                    </Badge>
                  )}
                  {Object.values(sourceCounts).every((v) => v === 0) && (
                    <span className="text-sm text-muted-foreground">
                      No child config to clone.
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Form inputs */}
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">New event name</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Spring Showcase 2026"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Start date</label>
                <Input
                  type="date"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">End date</label>
                <Input
                  type="date"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Info banner */}
          <div className="flex gap-2 rounded-md bg-blue-50 p-3 text-xs text-blue-700">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              This will copy pricing, divisions, extras, coupons, and form
              configs. Registrations, rosters, payments, and schedule constraints
              will NOT carry over. The clone starts as a draft.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={cloning}
          >
            Cancel
          </Button>
          <Button onClick={handleClone} disabled={cloning || !effectiveSourceId}>
            {cloning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Clone Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
